import { useState, useEffect } from "react";
import { Phone, Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { BRAND } from "@/lib/brand";

const NAV = [
  { label: "Services", href: "#services" },
  { label: "Work", href: "#work" },
  { label: "Process", href: "#how" },
  { label: "About", href: "#why" },
  { label: "Contact", href: "#contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#FFFFFF]/90 backdrop-blur-md border-b border-[#111613]/10 py-0"
          : "bg-transparent py-2"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 flex items-center justify-between h-16 md:h-20">
        <a
          href="#top"
          data-testid="header-logo"
          className="flex items-center gap-3 group"
        >
          <span className="font-display text-2xl md:text-[26px] text-[#0E2A1A] italic leading-none">
            El Monos
          </span>
          <span className="hidden sm:block font-mono-sm text-[#6B7A6E] pt-1 border-l border-[#111613]/20 pl-3">
            Landscaping
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-10 text-[#111613]">
          {NAV.map((n, i) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.href.replace("#", "")}`}
              className="nav-underline font-mono-sm text-[#111613]"
            >
              <span className="text-[#DA7B3C] mr-2">
                {String(i + 1).padStart(2, "0")}
              </span>
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <a
            href={`tel:${BRAND.phoneRaw}`}
            data-testid="header-call-button"
            className="hidden sm:inline-flex items-center gap-2 text-[#0E2A1A] hover:text-[#DA7B3C] transition-colors font-mono-sm"
          >
            <Phone className="w-3.5 h-3.5" strokeWidth={2} />
            {BRAND.phone}
          </a>
          <button
            data-testid="header-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 border border-[#111613]/30 text-[#111613] rounded-full"
            aria-label="Open menu"
          >
            {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            data-testid="header-mobile-menu"
            className="lg:hidden bg-[#FFFFFF] border-t border-[#111613]/10 overflow-hidden"
          >
            <nav className="flex flex-col px-5 py-5 gap-1">
              {NAV.map((n, i) => (
                <a
                  key={n.href}
                  href={n.href}
                  onClick={() => setOpen(false)}
                  className="py-3 font-mono-sm text-[#111613] border-b border-[#111613]/10"
                >
                  <span className="text-[#DA7B3C] mr-3">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {n.label}
                </a>
              ))}
              <a href={`tel:${BRAND.phoneRaw}`} className="mt-4 btn-primary w-full">
                <Phone className="w-4 h-4" /> {BRAND.phone}
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
